// src/hooks/useAutoLabels.js
// Screen readers (3.47.0): many boxes in the portal sit right under their
// label ("From Date", "Cover sheet message") without being linked to it, so
// a screen reader only says "edit text". This finds boxes with no name of
// their own and names them after the label just before them. It never
// changes a box that already has a name (aria-label, a linked or wrapping
// <label>, or a title); a box with only a placeholder gets its label too.
import { useEffect } from 'react';

const CONTROLS = 'input:not([type="hidden"]):not([type="checkbox"]):not([type="radio"]):not([type="button"]):not([type="submit"]), select, textarea';

function hasName(el) {
  // A placeholder ("e.g. 18005551234") is a hint, not a name: the label beside it wins
  if (el.getAttribute('aria-label') || el.getAttribute('aria-labelledby') || el.getAttribute('title')) return true;
  if (el.closest('label')) return true;
  return !!(el.id && document.querySelector(`label[for="${CSS.escape(el.id)}"]`));
}

// The label just before the box: a sibling, or its parent's sibling
function labelBefore(el) {
  for (let node = el, depth = 0; node && depth < 2; node = node.parentElement, depth++) {
    let prev = node.previousElementSibling;
    while (prev && !prev.textContent.trim()) prev = prev.previousElementSibling;
    if (prev && (prev.tagName === 'LABEL' || prev.tagName === 'LEGEND' || /^H[1-6]$/.test(prev.tagName))) return prev.textContent.replace(/\s+/g, ' ').trim().slice(0, 80);
  }
  return '';
}

function nameBoxes() {
  for (const el of document.querySelectorAll(CONTROLS)) {
    if (hasName(el)) continue;
    const text = labelBefore(el);
    if (text) el.setAttribute('aria-label', text);
  }
}

export function useAutoLabels(active) {
  useEffect(() => {
    if (!active || typeof MutationObserver === 'undefined') return undefined;
    let timer = null;
    const run = () => { clearTimeout(timer); timer = setTimeout(nameBoxes, 250); };
    run();
    const mo = new MutationObserver(run);
    mo.observe(document.body, { childList: true, subtree: true });
    return () => { clearTimeout(timer); mo.disconnect(); };
  }, [active]);
}
